import React, { useState, useEffect, useContext } from 'react';
import { fmtDateTime as _fmtDateTime } from '@/lib/dates';
import { toast } from 'sonner';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertTriangle, ShieldAlert, History, Clock } from 'lucide-react';

// Mandatory-reason modal for activate / deactivate of a Customer or Service Location.
export const StatusReasonDialog = ({ kind, entity, nextStatus, onClose, onDone }) => {
  const { token } = useContext(AuthContext);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const deactivating = nextStatus === 'inactive';
  const label = kind === 'customer' ? entity.name
    : kind === 'provider' ? entity.company_name : entity.location_name;
  const base = kind === 'customer' ? 'customers'
    : kind === 'provider' ? 'service-providers' : 'service-locations';

  const submit = async () => {
    if (!reason.trim()) { toast.error('A reason is required'); return; }
    setBusy(true);
    try {
      await axios.patch(`${API}/${base}/${entity.id}/status`,
        { status: nextStatus, reason: reason.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${deactivating ? 'Deactivated' : 'Reactivated'} ${label}`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update status');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="status-reason-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0E2A47] flex items-center gap-2">
            <AlertTriangle size={18} className={deactivating ? 'text-[#C8641E]' : 'text-[#5FA718]'} />
            {deactivating ? 'Deactivate' : 'Reactivate'} {kind === 'customer' ? 'Customer' : kind === 'provider' ? 'Service Provider' : 'Service Location'}
          </DialogTitle>
          <DialogDescription>
            {deactivating
              ? `Marking "${label}" as Inactive.${kind === 'customer' ? ' All of its service locations will also be deactivated.' : ''}`
              : `Restoring "${label}" to Active.`} A reason is required and will be recorded in the audit trail.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="status-reason">Reason <span className="text-red-500">*</span></Label>
          <Textarea id="status-reason" data-testid="status-reason-input" rows={3}
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder={deactivating ? 'e.g. Contract ended / account closed' : 'e.g. Contract renewed'} />
        </div>
        <DialogFooter className="gap-2">
          <button onClick={onClose} data-testid="status-reason-cancel"
            className="rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="status-reason-confirm"
            className={`rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${deactivating ? 'bg-[#C8641E] hover:bg-[#a9531a]' : 'bg-[#5FA718] hover:bg-[#4f8d14]'}`}>
            {busy ? 'Saving…' : (deactivating ? 'Deactivate' : 'Reactivate')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Status-change audit timeline (read-only).
export const HistoryDialog = ({ kind, entity, onClose }) => {
  const label = kind === 'customer' ? entity.name
    : kind === 'provider' ? entity.company_name : entity.location_name;
  const history = [...(entity.status_history || [])].reverse();
  const fmt = (iso) => {
    if (!iso) return '—';
    try { return _fmtDateTime(iso) || iso; } catch { return iso; }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0E2A47] flex items-center gap-2">
            <History size={18} className="text-[#8FC93A]" /> Status History — {label}
          </DialogTitle>
          <DialogDescription>Every activation and deactivation, with the reason and who made the change.</DialogDescription>
        </DialogHeader>

        {history.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-400" data-testid="history-empty">
            <Clock size={28} className="mx-auto mb-2 text-slate-300" />
            No status changes recorded yet. This record is still in its original state
            (<span className="font-semibold capitalize">{entity.status || (entity.is_active === false ? 'inactive' : 'active')}</span>).
          </div>
        ) : (
          <ol className="relative border-l border-gray-200 ml-2 pl-5 space-y-5 max-h-[55vh] overflow-y-auto" data-testid="history-timeline">
            {history.map((h, i) => {
              const active = h.status === 'active';
              return (
                <li key={i} className="relative" data-testid={`history-entry-${i}`}>
                  <span className={`absolute -left-[27px] top-1 h-3 w-3 rounded-full ring-4 ring-white ${active ? 'bg-[#5FA718]' : 'bg-[#C8641E]'}`} />
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${active ? 'bg-[#EAF6DD] text-[#4f8d14]' : 'bg-[#FBF3E4] text-[#C8641E]'}`}>
                      {active ? 'Reactivated' : 'Deactivated'}
                    </span>
                    <span className="text-xs text-slate-400">{fmt(h.changed_at)}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-700">{h.reason || '—'}</p>
                  <p className="text-xs text-slate-400">by {h.changed_by_name || 'Unknown'}</p>
                </li>
              );
            })}
          </ol>
        )}
        <DialogFooter>
          <button onClick={onClose} data-testid="history-close"
            className="rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F]">Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
export const PurgeEntityDialog = ({ kind, entity, onClose, onDone }) => {
  const { token } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [deps, setDeps] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const base = kind === 'customer' ? 'customers'
    : kind === 'provider' ? 'service-providers' : 'service-locations';
  const label = kind === 'customer' ? entity.name
    : kind === 'provider' ? entity.company_name : entity.location_name;

  useEffect(() => {
    axios.get(`${API}/${base}/${entity.id}/dependencies`, authHeaders)
      .then((r) => setDeps(r.data))
      .catch(() => setDeps({ has_dependencies: true, breakdown: {} }));
  }, [entity.id]);

  const blocked = deps?.has_dependencies;
  const canConfirm = deps && !blocked && confirmText.trim().toUpperCase() === 'PURGE' && !busy;

  const purge = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/${base}/${entity.id}`, authHeaders);
      toast.success(`Permanently purged ${label}`);
      onDone?.();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not purge'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="purge-entity-dialog">
        <DialogHeader>
          <DialogTitle className="text-red-600 flex items-center gap-2">
            <ShieldAlert size={18} /> Permanently purge {kind === 'customer' ? 'customer' : kind === 'provider' ? 'service provider' : 'service location'}?
          </DialogTitle>
          <DialogDescription>
            This permanently removes <strong>{label}</strong>. This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {!deps && <p className="text-sm text-slate-500">Checking linked records…</p>}

        {deps && blocked && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="purge-blocked">
            <p className="font-semibold mb-1">Purge blocked — linked records exist.</p>
            <ul className="list-disc pl-5 space-y-0.5">
              {Object.entries(deps.breakdown || {}).filter(([, n]) => n > 0).map(([k, n]) => (
                <li key={k}>{k.replace(/_/g, ' ')}: <strong>{n}</strong></li>
              ))}
            </ul>
            {(deps.work_order_numbers || []).length > 0 && (
              <p className="mt-2 text-xs" data-testid="purge-blocked-wos">
                Linked work orders: <strong>{deps.work_order_numbers.join(', ')}</strong>
              </p>
            )}
            <p className="mt-2">Deactivate it instead to preserve history.</p>
          </div>
        )}

        {deps && !blocked && (
          <div className="space-y-2">
            <p className="text-sm text-slate-600">No linked records found. Type <strong>PURGE</strong> to confirm permanent deletion.</p>
            <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
              data-testid="purge-confirm-input" placeholder="PURGE"
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm" />
          </div>
        )}

        <DialogFooter className="gap-2">
          <button onClick={onClose} data-testid="purge-entity-cancel"
            className="rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-gray-50">Cancel</button>
          <button onClick={purge} disabled={!canConfirm} data-testid="purge-entity-confirm"
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
            {busy ? 'Purging…' : 'Purge Permanently'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
